import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateConnectSessionApiRequest } from '@momentum/types';
import { integrationsService } from '../services/integrations.service';

export const useIntegrations = (companyId: string) => {
  return useQuery({
    queryKey: ['integrations', companyId],
    queryFn: () => integrationsService.getAllIntegrations(companyId),
    enabled: !!companyId,
  });
};

export const useAdminIntegrationStatus = (enabled = true) => {
  return useQuery({
    queryKey: ['integrations', 'admin-status'],
    queryFn: () => integrationsService.getAdminIntegrationStatus(),
    enabled,
    staleTime: 30_000,
  });
};

export const useCreateConnectSession = () => {
  return useMutation({
    mutationFn: (body: CreateConnectSessionApiRequest) =>
      integrationsService.createConnectSession(body),
  });
};

export const useDeleteIntegration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      integrationId,
    }: {
      companyId: string;
      integrationId: string;
    }) => integrationsService.deleteIntegration(companyId, integrationId),
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: ['integrations', companyId] });
    },
  });
};

export const useRegisterWebhook = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      integrationId,
    }: {
      companyId: string;
      integrationId: string;
    }) => integrationsService.registerWebhook(companyId, integrationId),
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({
        queryKey: ['integrations', companyId],
      });
    },
  });
};

export const useSyncIntegration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      integrationId,
    }: {
      companyId: string;
      integrationId: string;
    }) => integrationsService.syncIntegration(companyId, integrationId),
    onSuccess: (_, { companyId }) => {
      // Invalidate company hierarchy queries to refresh goals tree
      queryClient.invalidateQueries({ queryKey: ['company-hierarchy'] });
      // Invalidate goals and issues queries to refetch after sync
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({
        queryKey: ['integrations', companyId],
      });
      // Invalidate user-assigned-goals for one-on-one review
      queryClient.invalidateQueries({
        queryKey: ['user-assigned-goals'],
      });
    },
  });
};
